import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Hr from "@saleor/components/Hr";
import {
  validateFileSize,
  validateImageDimensions,
  validateMimeTypes
} from "@saleor/coupons/validators";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { commonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { CouponCreateFormData } from "../CouponCreatePage";
import { TooltipArrowLeft } from "../CouponDetails/Tooltip";
const useStyles = makeStyles(
  theme => ({
    card: {
      height: "400px",
      marginTop: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        marginTop: 0
      }
    },
    descriptionLabel: {
      margin: "5px 0px 5px 0px"
    },
    fileField: {
      display: "none"
    },
    fileUploadField: {
      padding: "0!important"
    },
    guidelines: {
      fontWeight: "bold"
    },
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      position: "relative",
      width: "100%"
    },
    imageGridContainer: {
      justifyContent: "center"
    },
    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: "100%",
      left: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageOverlayShow: {
      "&$imageOverlay": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      }
    },
    imageOverlayToolbar: {
      display: "flex",
      position: "absolute",
      right: 0,
      top: 0
    },
    imageflex: {},
    info: {
      color: "#0937f4",
      fontSize: 17,
      fontWeight: "bold"
    },
    infoGuidelines: {
      fontSize: 13,
      marginLeft: "10px",
      padding: "5px 0 0 0"
    },
    infoOutlinedIcon: {
      marginLeft: theme.spacing(1)
    },
    infoPanel: {
      padding: "20px 40px 0 40px"
    },
    infoPanelTitle: {
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(4),
      justifyContent: "flex-start",
      marginBottom: 10
    },
    mainPromoTitleContainer: {
      alignItems: "center",
      display: "flex"
    },
    photosIcon: {
      height: "64px",
      margin: "0 auto",
      width: "64px"
    },
    uploadButton: {
      display: "block"
    },
    uploadRoot: {
      fontSize: 12,
      fontWeight: 600,
      padding: "15px",
      textAlign: "center"
    }
  }),
  { name: "CouponsPromoImage" }
);

interface CouponImageProps {
  data: CouponCreateFormData;
  loading?: boolean;
  onChange: FormChange;
  errors: any;
  disabled?: boolean;
  onPromoImageUpload?: (file: File) => void;
}

export const CouponsPromoImage: React.FC<CouponImageProps> = props => {
  const classes = useStyles(props);
  const intl = useIntl();
  const upload = React.useRef(null);
  const notify = useNotifier();
  const { data, loading, onChange, onPromoImageUpload, disabled } = props;

  const [promoUpload, setPromoUpload] = React.useState(null);
  const [promoImage, setPromoImage] = React.useState(null);

  const checkFile = async e => {
    const file = e[0];
    if (!file) {
      return false;
    }
    try {
      await validateImageDimensions(file, {
        height: { max: 640, min: 440 },
        width: { max: 1080, min: 720 }
      });
      await validateFileSize(file);
      await validateMimeTypes(file);

      setPromoUpload(e);
      onPromoImageUpload(e);
      onChange({
        target: {
          name: "promoImage",
          value: file
        }
      });
    } catch (error) {
      notify({
        status: "error",
        text: error,
        title: "Can't Upload."
      });
    }
  };

  const cardTitle = (
    <div className={classes.infoPanelTitle}>
      <strong>
        {intl.formatMessage({
          defaultMessage: "Main Promo",
          description: "Coupon Main Promo"
        })}
      </strong>
      <TooltipArrowLeft
        title="This image will appear before the coupon is saved for later or redeemed."
        placement="right"
      >
        <InfoOutlined color="primary" className={classes.infoOutlinedIcon} />
      </TooltipArrowLeft>
    </div>
  );

  React.useEffect(() => {
    if (promoUpload) {
      setPromoImage(URL.createObjectURL(promoUpload[0]));
      if (onPromoImageUpload && typeof onPromoImageUpload === "function") {
        onPromoImageUpload(promoUpload[0]);
      }
    }
  }, [promoUpload]);

  return (
    <Card className={classes.card}>
      <CardTitle
        title={cardTitle}
        toolbar={
          <>
            <Button
              onClick={() => upload.current.click()}
              disabled={loading || disabled}
              variant="text"
              id="promoImageUploadBtn"
              color="primary"
              data-test="button-upload-image"
              className={classes.uploadButton}
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="promoImageUploadinput"
              onChange={e => checkFile(e.target.files)}
              type="file"
              ref={upload}
              accept="image/jpeg, image/jpg, image/png"
            />
          </>
        }
      />
      <div
        className={classNames(classes.imageGridContainer, {
          [classes.imageflex]: promoImage || data.promoImage
        })}
      >
        <CardContent>
          <Dropzone onDrop={checkFile}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent className={classes.fileUploadField}>
                  <input
                    {...getInputProps()}
                    id="promoImageUploadDropzone"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                    disabled={disabled}
                  />
                  {promoImage || data.promoImage ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                upload.current.value = null;
                                setPromoImage(null);
                                onPromoImageUpload(null);
                                onChange({
                                  target: {
                                    name: "promoImage",
                                    value: null
                                  }
                                });
                              }}
                              disabled={disabled}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        className={classes.image}
                        src={promoUpload ? promoImage : data.promoImage}
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>
        <Hr />
        <div className={classes.infoPanel}>
          <Grid>
            <div className={classes.infoPanelTitle}>
              <InfoOutlined color="primary" />
              <Typography className={classes.info}>
                Upload Guidelines:
              </Typography>
            </div>
          </Grid>
          <div>
            <span className={classes.infoGuidelines}>
              {" "}
              1. Image Width Dimension:{" "}
              <span className={classes.guidelines}>720px to 1080px.</span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              2. Image Height Dimension:{" "}
              <span className={classes.guidelines}>440px to 640px.</span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              3. Image file should{" "}
              <span className={classes.guidelines}>
                not be greater than 2MB.
              </span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              4. Only{" "}
              <span className={classes.guidelines}>
                JPG, JPEG and PNG files
              </span>{" "}
              are acceptable.
            </span>
          </div>
          <CardContent></CardContent>
        </div>
      </div>
    </Card>
  );
};

export default CouponsPromoImage;
